import React from 'react';
import { cn } from '../../utils/cn';

/* -------------------------------------------------------------------------- */
/*  Heading                                                                   */
/* -------------------------------------------------------------------------- */

const HEADING_SIZES = {
  1: 'text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]',
  2: 'text-3xl font-bold tracking-tight leading-tight',
  3: 'text-2xl font-bold tracking-tight leading-snug',
  4: 'text-xl font-semibold tracking-tight leading-snug',
  5: 'text-lg font-semibold leading-snug',
  6: 'text-base font-semibold leading-snug',
};

export function Heading({ level = 1, as, className, children, ...rest }) {
  const Tag = as || `h${level}`;
  return (
    <Tag className={cn(HEADING_SIZES[level], 'text-text', className)} {...rest}>
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/*  Display — gros titre éditorial                                            */
/* -------------------------------------------------------------------------- */

export function Display({ size = 'md', as: Tag = 'h1', className, children, ...rest }) {
  const sizeCls = {
    sm: 'text-4xl',
    md: 'text-5xl',
    lg: 'text-6xl',
    xl: 'text-7xl',
  }[size];
  return (
    <Tag className={cn(sizeCls, 'font-extrabold tracking-tight leading-[0.95] text-text', className)} {...rest}>
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/*  Text — paragraphe / span                                                  */
/* -------------------------------------------------------------------------- */

const TEXT_SIZES = {
  xs:  'text-xs',
  sm:  'text-sm',
  md:  'text-base',
  lg:  'text-md',
  xl:  'text-lg',
};

const TEXT_TONES = {
  default: 'text-text',
  muted:   'text-mute',
  faded:   'text-mute-2',
  ink:     'text-ink',
  success: 'text-success',
  warn:    'text-warn',
  danger:  'text-danger',
  info:    'text-info',
};

export function Text({ as: Tag = 'p', size = 'md', tone = 'default', weight, italic, mono, serif, className, children, ...rest }) {
  const family = mono ? 'font-mono' : serif ? 'font-serif' : 'font-sans';
  const weightCls = weight ? `font-${weight}` : '';
  return (
    <Tag
      className={cn(family, TEXT_SIZES[size], TEXT_TONES[tone], weightCls, italic && 'italic', 'leading-relaxed', className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/*  Lead — intro de section                                                   */
/* -------------------------------------------------------------------------- */

export function Lead({ className, children, ...rest }) {
  return (
    <p className={cn('text-md md:text-lg text-text-2 leading-relaxed', className)} {...rest}>
      {children}
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/*  Eyebrow — petit label mono UPPERCASE                                      */
/* -------------------------------------------------------------------------- */

export function Eyebrow({ as: Tag = 'div', className, children, ...rest }) {
  return (
    <Tag className={cn('ds-eyebrow', className)} {...rest}>
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/*  Code — inline                                                             */
/* -------------------------------------------------------------------------- */

export function Code({ className, children, ...rest }) {
  return (
    <code className={cn('font-mono text-[0.92em] px-1 py-0.5 rounded-sm bg-bg-2 text-text border border-border', className)} {...rest}>
      {children}
    </code>
  );
}

/* -------------------------------------------------------------------------- */
/*  Quote                                                                     */
/* -------------------------------------------------------------------------- */

export function Quote({ author, className, children, ...rest }) {
  return (
    <blockquote className={cn('border-l-2 border-ink pl-4 my-4', className)} {...rest}>
      <p className="font-serif italic text-lg leading-snug text-text">— {children}</p>
      {author && <footer className="mt-2 text-sm text-mute">— {author}</footer>}
    </blockquote>
  );
}

/* -------------------------------------------------------------------------- */
/*  exports                                                                   */
/* -------------------------------------------------------------------------- */

export default { Heading, Display, Text, Lead, Eyebrow, Code, Quote };
